// PATCH /wb/company-settings — Phase C5/C6.
//
// Read access goes through WbCompanySettingsContext (one cache, one
// refetch, shared by VAT + Compliance pages). This file owns ONLY the
// partial-PATCH mutation; pages compose it with the context so each
// save touches only the fields it owns and then triggers a single
// context refetch instead of two independent cache invalidations.
//
// Input shape (`UpdateWbCompanySettingsInput`):
//   • Every field is optional — the server treats the body as a
//     partial update. A page that owns only the C5 (VAT) fields must
//     NEVER include the C6 (compliance) fields and vice versa.
//   • `default_vat_rate_id` is the enc_id of a WbVatRate row (string)
//     or `null` to clear. The server response embeds the full nested
//     object as `default_vat_rate` instead.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { WbCompanySettingsDto } from '../context/WbCompanySettingsContext';

export interface UpdateWbCompanySettingsInput {
  // VAT (C5)
  vat_enabled?: boolean;
  vat_registration_number?: string | null;
  default_vat_rate_id?: string | null;

  // Operational policy (C6)
  credit_limit_behaviour?: 'block' | 'warn' | 'off';
  trade_approved_default?: boolean;
  manual_weight_requires_reason_hw_online?: boolean;
  manual_weight_requires_reason_hw_offline?: boolean;
  stale_tare_threshold_days?: number;
  compliance_wtn_enabled?: boolean;
  compliance_hazardous_enabled?: boolean;
  compliance_carrier_licence_required?: boolean;
  compliance_ewc_required_on_waste_lines?: boolean;
  stock_alert_email_recipients?: string[];
  weight_reconciliation_tolerance_pct?: number;
  autosave_draft_interval_sec?: number;
}

export function useUpdateWbCompanySettings() {
  const qc = useQueryClient();
  return useMutation<
    { company_settings: WbCompanySettingsDto },
    Error,
    UpdateWbCompanySettingsInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { company_settings: WbCompanySettingsDto },
        UpdateWbCompanySettingsInput
      >(endpoints.wb.companySettings, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.companySettings });
    },
  });
}
